import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Container,
  Heading,
  SimpleGrid,
  Spinner,
  Stack,
  Text,
} from '@chakra-ui/react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import EventCard from '../EventCard';

const byDateAsc = (a, b) => {
  const dateA = new Date(a?.Data || 0).getTime();
  const dateB = new Date(b?.Data || 0).getTime();
  return dateA - dateB;
};

export default function DynamicEventsSection({ config, sectionId }) {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasError, setHasError] = useState(false);

  const filters = useMemo(() => config?.events?.filters || {}, [config]);
  const isEnabled = Boolean(config?.events?.enabled);

  useEffect(() => {
    if (!isEnabled) return undefined;

    const controller = new AbortController();
    const params = new URLSearchParams({
      past: 'false',
      page: '1',
      pageSize: '6',
    });
    Object.entries(filters).forEach(([key, value]) => {
      if (value) params.append(key, value);
    });

    const loadEvents = async () => {
      setLoading(true);
      setHasError(false);
      try {
        const response = await fetch(`/api/eventi?${params.toString()}`, {
          signal: controller.signal,
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const data = await response.json();
        const nextEvents = Array.isArray(data?.events) ? data.events : [];
        setEvents(nextEvents.sort(byDateAsc));
      } catch (error) {
        if (error?.name !== 'AbortError') {
          setHasError(true);
          setEvents([]);
        }
      } finally {
        setLoading(false);
      }
    };

    loadEvents();
    return () => controller.abort();
  }, [filters, isEnabled]);

  if (!isEnabled) return null;

  const fallback = config?.fallback || {};
  const showFallback = !loading && (hasError || events.length === 0);

  return (
    <Box
      id={sectionId || undefined}
      scrollMarginTop={sectionId ? '200px' : undefined}
      bg="white"
      py={{ base: 10, md: 14 }}
    >
      <Container maxW="container.lg">
        <Stack spacing={6}>
          <Heading as="h2" size="lg" color="#3b235a">
            {config?.events?.title || 'Eventi in evidenza'}
          </Heading>

          {loading ? (
            <Stack direction="row" align="center">
              <Spinner color="#36a9e1" />
              <Text>Caricamento eventi...</Text>
            </Stack>
          ) : null}

          {!loading && events.length > 0 ? (
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={5}>
              {events.map((event) => (
                <Box
                  key={event.documentId || event.urlTitle}
                  cursor={event?.urlTitle ? 'pointer' : 'default'}
                  onClick={
                    event?.urlTitle
                      ? () => navigate(`/event/${event.urlTitle}`)
                      : undefined
                  }
                  _hover={
                    event?.urlTitle ? { transform: 'translateY(-2px)' } : {}
                  }
                  transition="transform 0.2s ease"
                >
                  <EventCard event={event} hideButton withMargin={false} />
                </Box>
              ))}
            </SimpleGrid>
          ) : null}

          {showFallback ? (
            <Alert
              status={hasError ? 'warning' : 'info'}
              borderRadius="md"
              alignItems="start"
            >
              <AlertIcon />
              <Stack spacing={2} align="start">
                <Heading as="h3" size="sm">
                  {fallback.title || 'Contenuti in arrivo'}
                </Heading>
                <Text>
                  {fallback.text || 'Torna presto per nuovi contenuti.'}
                </Text>
                {fallback.ctaHref && fallback.ctaLabel ? (
                  <Button
                    as={RouterLink}
                    to={fallback.ctaHref}
                    size="sm"
                    colorScheme="blue"
                  >
                    {fallback.ctaLabel}
                  </Button>
                ) : null}
              </Stack>
            </Alert>
          ) : null}
        </Stack>
      </Container>
    </Box>
  );
}
